import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AlertTitle from '@mui/material/AlertTitle';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Banner } from '@src/common/components/Banner';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { PreauthorizationProvider } from '@src/orders/components/preauthorization/PreauthorizationProvider';
import { OrderStatus } from '@src/orders/domain/Order';
import type { OrderListing } from '@src/orders/domain/OrderList';
import { PreauthStatus } from '@src/orders/domain/PreauthStatus';
import { t } from 'i18next';
import { Trans } from 'react-i18next';

interface Props {
  order: OrderListing;
}

const mustHideBanner = (order: OrderListing): boolean => {
  return [
    OrderStatus.CANCELLED,
    OrderStatus.COMPLETED,
    OrderStatus.TO_INVOICE,
    OrderStatus.INVOICED,
    OrderStatus.TO_REVIEW,
  ].includes(order.status);
};

export const PreauthorizationBanner = ({ order }: Props) => {
  const { user } = useAuthentication();

  if (mustHideBanner(order)) {
    return null;
  }

  if (order.details.payment.status === PreauthStatus.Preauthorized) {
    return (
      <Banner
        data-testid="preauthorization-banner"
        severity="info"
        style={{ width: '100%' }}
      >
        <InfoOutlinedIcon />

        <Stack flex={1}>
          <AlertTitle>
            {t('preauthorization.preauthorizationTransactions')}
          </AlertTitle>

          <Typography>
            <Trans
              i18nKey={
                isClientUser(user)
                  ? 'preauthorization.preauthorizationTransactionsAmount'
                  : 'preauthorization.preauthorizationTransactionsDetails'
              }
              values={{
                amount: order.details.payment.settledAmount,
                currency: order.details.payment.currency,
              }}
              components={{ strong: <strong style={{ fontWeight: 700 }} /> }}
            />
          </Typography>

          {!isClientUser(user) && <PreauthorizationProvider order={order} />}
        </Stack>
      </Banner>
    );
  }

  if (isClientUser(user)) {
    return null;
  }

  if (order.details.payment.status === PreauthStatus.PreauthorizationRequired) {
    return (
      <Banner severity="error" style={{ width: '100%' }}>
        <InfoOutlinedIcon />
        <Stack flex={1}>
          <AlertTitle>
            {t('preauthorization.preauthorizationRequired')}
          </AlertTitle>
          <Typography>
            {t('preauthorization.preauthorizationRequiredDetails')}
          </Typography>
          <PreauthorizationProvider order={order} />
        </Stack>
      </Banner>
    );
  }

  if (order.details.payment.status === PreauthStatus.MissingPaymentMethod) {
    return (
      <Banner severity="error" style={{ width: '100%' }}>
        <InfoOutlinedIcon />
        <Stack flex={1}>
          <AlertTitle>{t('preauthorization.missingPaymentMethod')}</AlertTitle>
          <Typography>
            {t('preauthorization.missingPaymentMethodDetails')}
          </Typography>
        </Stack>
      </Banner>
    );
  }

  return null;
};
